import type { Booking, Frequency, JobStatus } from "../types";

const STATUSES: Booking["status"][] = ["Completed", "Pending", "Confirmed", "new_request", "Deleted"];

function isBookingStatus(v: unknown): v is Booking["status"] {
  return typeof v === "string" && (STATUSES as readonly string[]).includes(v);
}

/** API/list responses may use string enums or legacy numeric wire codes (see `bookingWireCodec`). */
export function normalizeBookingStatusFromApi(v: unknown): Booking["status"] | null {
  if (isBookingStatus(v)) return v;
  if (v === 1 || v === "1") return "Pending";
  if (v === 2 || v === "2") return "Confirmed";
  if (v === 3 || v === "3") return "Completed";
  if (v === 4 || v === "4") return "new_request";
  if (v === 5 || v === "5") return "Deleted";
  if (typeof v === "string") {
    const lower = v.trim().toLowerCase().replace(/\s+/g, "_");
    const byLower: Record<string, Booking["status"]> = {
      pending: "Pending",
      confirmed: "Confirmed",
      completed: "Completed",
      new_request: "new_request",
      deleted: "Deleted",
    };
    if (lower in byLower) return byLower[lower]!;
  }
  return null;
}

const PAYMENTS: Booking["paymentStatus"][] = ["Paid", "Unpaid", "Verification Pending"];

function isPaymentStatus(v: unknown): v is Booking["paymentStatus"] {
  return typeof v === "string" && (PAYMENTS as readonly string[]).includes(v);
}

const FREQS: Frequency[] = ["One-time", "Weekly", "Bi-weekly", "Monthly"];

function isFrequency(v: unknown): v is Frequency {
  return typeof v === "string" && (FREQS as readonly string[]).includes(v);
}

const JOB_STATUSES: JobStatus[] = ["Scheduled", "En Route", "In Progress", "Completed"];

function isJobStatus(v: unknown): v is JobStatus {
  return typeof v === "string" && (JOB_STATUSES as readonly string[]).includes(v);
}

function isPlainRecord(v: unknown): v is Record<string, unknown> {
  return v !== null && typeof v === "object" && !Array.isArray(v);
}

/**
 * Narrow an API/Firestore-shaped object to Booking with safe defaults.
 */
export function narrowBookingFromApi(value: unknown): Booking | null {
  if (!isPlainRecord(value)) return null;
  const o = value;
  const id = typeof o.id === "string" ? o.id.trim() : "";
  if (!id) return null;

  const rawDate = typeof o.rawDate === "string" ? o.rawDate : "";
  const rawTime = typeof o.rawTime === "string" ? o.rawTime : "";
  const service = typeof o.service === "string" ? o.service : "";
  const date = typeof o.date === "string" ? o.date : rawDate ? rawDate : "";
  const time = typeof o.time === "string" ? o.time : rawTime ? rawTime : "";

  const status: Booking["status"] = isBookingStatus(o.status) ? o.status : "Confirmed";
  const paymentStatus: Booking["paymentStatus"] = isPaymentStatus(o.paymentStatus)
    ? o.paymentStatus
    : "Unpaid";
  const frequency: Frequency = isFrequency(o.frequency) ? o.frequency : "One-time";

  let price: number | undefined;
  if (typeof o.price === "number" && Number.isFinite(o.price)) price = o.price;
  else if (typeof o.price === "string" && o.price.trim() !== "") {
    const n = Number(o.price);
    if (Number.isFinite(n)) price = n;
  }

  const row: Booking = {
    id,
    service,
    date,
    time,
    rawDate,
    rawTime,
    status,
    paymentStatus,
    frequency,
    ...(price !== undefined ? { price } : {}),
    ...(typeof o.userName === "string" ? { userName: o.userName } : {}),
    ...(typeof o.userEmail === "string" ? { userEmail: o.userEmail } : {}),
    ...(typeof o.userPhone === "string" ? { userPhone: o.userPhone } : {}),
    ...(typeof o.address === "string" ? { address: o.address } : {}),
    ...(o.assignedTo === null || typeof o.assignedTo === "string"
      ? { assignedTo: o.assignedTo as string | null }
      : {}),
    ...(isJobStatus(o.jobStatus) ? { jobStatus: o.jobStatus } : {}),
    ...(typeof o.accessCode === "string" ? { accessCode: o.accessCode } : {}),
    ...(typeof o.houseInfo === "string" ? { houseInfo: o.houseInfo } : {}),
    ...(typeof o.petAlert === "string" ? { petAlert: o.petAlert } : {}),
    ...(typeof o.rating === "number" && Number.isFinite(o.rating) ? { rating: o.rating } : {}),
    ...(typeof o.review === "string" ? { review: o.review } : {}),
    ...(typeof o.reviewRequestSentAt === "string" ? { reviewRequestSentAt: o.reviewRequestSentAt } : {}),
    ...(typeof o.notes === "string" ? { notes: o.notes } : {}),
    ...(typeof o.preferredDate === "string" ? { preferredDate: o.preferredDate } : {}),
    ...(typeof o.active === "boolean" ? { active: o.active } : {}),
    ...(o.deletedAt === null || typeof o.deletedAt === "string" ? { deletedAt: o.deletedAt } : {}),
    ...(typeof o.statusBeforeDelete === "string" ? { statusBeforeDelete: o.statusBeforeDelete } : {}),
    ...(o.paymentMethod === "stripe" || o.paymentMethod === "zelle"
      ? { paymentMethod: o.paymentMethod }
      : {}),
  };

  return row;
}

export function narrowBookingsFromApiList(rows: readonly unknown[]): Booking[] {
  const out: Booking[] = [];
  for (const raw of rows) {
    const b = narrowBookingFromApi(raw);
    if (b) out.push(b);
  }
  return out;
}
